import { NavLink, useNavigate } from 'react-router-dom';
import {
  IconFiles,
  IconBox,
  IconCalendarEvent,
  IconChartHistogram,
} from '@tabler/icons-react';
import { C, RADIUS } from '../theme';
import Logo from './Logo';
import OrgSwitcher from './OrgSwitcher';
import { HouseIcon, MicIcon } from './InlineIcons';
import { useVoice } from './VoiceModal';
import { useDraftPO } from './DraftPOFlow';
import { useOrg } from './OrgContext';
import { useT } from '../i18n';
import SharedHeader from './SharedHeader';
import { isRedesign } from '../lib/redesign';

// Reports replaces Dashboard as a top-level nav square (Dashboard is still
// reachable at /dashboard, just not surfaced here). Four squares.
const TABS = [
  { to: '/cases', labelKey: 'nav.cases', Icon: IconFiles },
  { to: '/inventory', labelKey: 'nav.inventory', Icon: IconBox },
  { to: '/schedule', labelKey: 'nav.schedule', Icon: IconCalendarEvent },
  { to: '/reports', labelKey: 'nav.reports', Icon: IconChartHistogram },
];

function LegacyHeader() {
  const navigate = useNavigate();
  const { openVoice, voiceActive } = useVoice();
  const { pendingCount } = useDraftPO();
  const { can } = useOrg();
  const t = useT();

  return (
    <div>
      <div
        style={{
          padding: '14px 32px 0',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        <div style={{ justifySelf: 'start', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Logo size={30} wordmarkSize={21} />
          <OrgSwitcher />
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            justifySelf: 'center',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '9px 22px',
            borderRadius: RADIUS.md,
            background: C.card,
            border: `1px solid ${C.border}`,
            fontSize: 14,
            color: C.textDark,
            fontWeight: 500,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            fontFamily: 'inherit',
          }}
        >
          <HouseIcon size={15} color={C.textMuted} />
          {t('nav.home')}
        </button>

        <div style={{ justifySelf: 'end' }}>
          <button
            type="button"
            onClick={openVoice}
            aria-label={t('nav.voice')}
            style={{
              position: 'relative',
              width: 34,
              height: 34,
              borderRadius: RADIUS.md,
              background: C.orange,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <MicIcon size={16} color="#fff" />
            {voiceActive && (
              // green dot = a voice conversation is still alive (minimized)
              <span
                aria-label="Conversation in progress"
                style={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: C.green,
                  border: `2px solid ${C.bg}`,
                }}
              />
            )}
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
          padding: '16px 32px 18px',
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        {TABS.filter((tab) => tab.to !== '/reports' || can('reports.financial')).map(({ to, labelKey, Icon }) => (
          <NavLink
            key={to}
            to={to}
            style={({ isActive }) => ({
              padding: '7px 16px',
              borderRadius: RADIUS.md,
              background: C.bg,
              border: `1px solid ${isActive ? C.orange : C.border}`,
              fontSize: 12,
              color: isActive ? C.orange : C.textMuted,
              fontWeight: isActive ? 500 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              textDecoration: 'none',
            })}
          >
            <Icon size={14} stroke={2} />
            {t(labelKey)}
            {to === '/inventory' && pendingCount > 0 && (
              <span
                aria-label={t('nav.readyToOrder', { n: pendingCount })}
                style={{
                  marginLeft: 2,
                  minWidth: 16,
                  height: 16,
                  padding: '0 5px',
                  borderRadius: 8,
                  background: C.orange,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {pendingCount}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

// Every page renders this one component. With the redesign switched on (dev website only) it is the shared header of
// Leo's design; otherwise it is today's header, unchanged.
export default function AppHeader() {
  return isRedesign() ? <SharedHeader /> : <LegacyHeader />;
}
